import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { MfaService } from '../../../platform/services/mfa-service/mfa.service';
import { SpinnerState } from '../../state-stores/spinner.state';

/**
 * Route guard enforcing our own (non-Entra) MFA: blocks activation of any protected route until
 * the user's MFA status has been confirmed, redirecting to the MFA challenge page if it's
 * required and not yet verified in this browser session.
 *
 * Running this as a `canActivate` guard - rather than reacting after the fact once the
 * destination component has already been instantiated - means the router outlet never renders
 * that component's content at all while MFA is outstanding, so there's no flash/glimpse of it
 * before being redirected to /mfa.
 *
 * @example
 * {
 *   path: 'stationapp',
 *   loadChildren: () => import('./flowpoint-apps/station-app/station-app.routes').then(x => x.stationAppRoutes),
 *   canActivate: [mfaGuard],
 * }
 */
export const mfaGuard: CanActivateFn = async (_route, state): Promise<boolean | UrlTree> => {
  const mfaService = inject(MfaService);
  const router = inject(Router);
  const spinnerState = inject(SpinnerState);

  spinnerState.showBusySpinner();
  try {
    const status = await mfaService.getStatus();
    if (status.required && !status.verified) {
      return router.createUrlTree(['/mfa'], { queryParams: { returnUrl: state.url } });
    }
  } catch (err) {
    // If the status check itself fails, fall through and allow navigation - the GraphQL MFA
    // gate (and the global Apollo error link) will still catch MFA_REQUIRED if needed.
    console.error('[MFA] Status check failed, allowing navigation without MFA gate:', err);
  } finally {
    spinnerState.hideBusySpinner();
  }

  return true;
};
